import { isSodimacStandalone } from '@digital-retail/store-manager';

/**
 * Log error utility to avoid direct console usage
 * @param {string} message - Error message
 * @param {Error} error - Error object
 */
export const logError = (message, error) => {
  /* eslint-disable-next-line no-console */
  console.error(message, error);
};

/**
 * Event configurations and youtube start
 * @param {HTMLIFrameElement} iframe
 * @param {Function} setVideoPlaying
 * @returns {Object|null}
 */
export const initYoutubePlayer = (iframe, setVideoPlaying) => {
  if (!iframe || !window.YT || !window.YT.Player) {
    return null;
  }

  try {
    const player = new window.YT.Player(iframe, {
      events: {
        onStateChange: (event) => {
          if (window.YT && window.YT.PlayerState) {
            setVideoPlaying(event.data === window.YT.PlayerState.PLAYING);
          }
        },
      },
    });

    return player;
  } catch (error) {
    logError('Error initializing YouTube player:', error);
    return null;
  }
};

/**
 * Stop video player
 * @param {HTMLIFrameElement} iframe
 * @returns {void}
 */
export const stopVideo = (playerOrIframe) => {
  if (!playerOrIframe) return;

  try {
    if (playerOrIframe.stopVideo) {
      playerOrIframe.stopVideo();
    } else if (playerOrIframe.src) {
      const { src } = playerOrIframe;
      // eslint-disable-next-line no-param-reassign
      playerOrIframe.src = src;
    }
  } catch (error) {
    logError('Error stopping video:', error);
  }
};

/**
 * Extracts youtube video ID
 * @param {string} url
 * @returns {string}
 */
export const extractVideoId = (url) => {
  if (!url) return '';

  if (url.includes('embed/')) {
    return url.split('embed/')[1]?.split('?')[0] || '';
  }
  if (url.includes('watch?v=')) {
    return url.split('watch?v=')[1]?.split('&')[0] || '';
  }
  if (url.includes('youtu.be/')) {
    return url.split('youtu.be/')[1]?.split('?')[0] || '';
  }

  return '';
};

/**
 * URL Params for youtube iframe
 * @param {boolean} isMobile
 * @param {string} videoId
 * @returns {string}
 */
export const getYoutubeParams = (isMobile, videoId, store) => {
  const commonParams = ['rel=0', 'color=white', 'loop=1', `playlist=${videoId}`];
  let storeParams = ['mute=1', 'controls=0', 'autoplay=1'];
  if (isSodimacStandalone(store)) {
    storeParams = ['mute=0', 'controls=1', 'autoplay=0'];
  }

  const mobileParams = ['playsinline=1', 'fs=0'];

  const desktopParams = [];

  const allParams = [...commonParams, ...storeParams, ...(isMobile ? mobileParams : desktopParams)].join('&');

  return `?${allParams}`;
};

/**
 * Touch guards for mobile devices
 * @param {HTMLElement} container
 */
export const setupTouchGuards = (container) => {
  if (!container) return;
  const existingGuards = container.querySelectorAll('.touch-guard');
  existingGuards.forEach((guard) => guard.remove());
  const leftGuard = document.createElement('div');
  leftGuard.className = 'touch-guard touch-guard-left';
  leftGuard.style.position = 'absolute';
  leftGuard.style.top = '0';
  leftGuard.style.left = '0';
  leftGuard.style.width = '40px';
  leftGuard.style.height = '100%';
  leftGuard.style.zIndex = '15';
  leftGuard.style.opacity = '0';
  leftGuard.style.pointerEvents = 'none';
  container.appendChild(leftGuard);
};

/**
 * Remove touch guards
 * @param {HTMLElement} container
 */
export const cleanupTouchGuards = (container) => {
  if (!container) return;

  const guards = container.querySelectorAll('.touch-guard');
  guards.forEach((guard) => guard.remove());
};
